import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
    LogOut,
    RefreshCw,
    Mail,
    Phone,
    Building2,
    Calendar,
    Search,
    X,
    Lock,
    Loader2,
    Inbox,
    BookOpen,
    CheckCircle2,
    AlertCircle,
    Trash2,
    Eye,
    EyeOff,
    MailWarning,
    Send,
} from "lucide-react";
import { toast } from "sonner";
import {
    adminLogin,
    adminLogout,
    fetchCurrentAdmin,
    fetchInquiries,
    fetchAdminStats,
    updateInquiryStatus,
    deleteInquiry,
    bulkDeleteInquiries,
    deleteAllInquiries,
    resendInquiryEmail,
} from "@/lib/admin-api";

const SOURCES = [
    { id: "", label: "All sources" },
    { id: "general", label: "General Inquiry" },
    { id: "consultation", label: "Consultation" },
    { id: "pricing", label: "Pricing" },
    { id: "services", label: "Services" },
    { id: "checklist-yearend", label: "Year-End Checklist Lead" },
    { id: "checklist-vat-invoice", label: "VAT Invoice Checklist Lead" },
    { id: "tax-number-form", label: "Tax Number Form" },
    { id: "tax-number-skip-pdf", label: "Tax Number — Skip the PDF" },
    { id: "it77-electronic", label: "IT77 Electronic Form" },
    { id: "matchmaker", label: "Package Matchmaker" },
    { id: "structures", label: "Entity Structures Wizard" },
    { id: "ringfencing", label: "Ring-Fencing Checklist" },
    { id: "tax-calculator", label: "Tax Calculator" },
];

const STATUSES = [
    { id: "", label: "All statuses" },
    { id: "new", label: "New" },
    { id: "read", label: "Read" },
    { id: "responded", label: "Responded" },
];

const SOURCE_LABEL = Object.fromEntries(SOURCES.filter((s) => s.id).map((s) => [s.id, s.label]));
const STATUS_TONE = {
    new: { bg: "bg-tca-orangeSoft", text: "text-tca-orange", border: "border-tca-orange" },
    read: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-300" },
    responded: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300" },
};

const formatDate = (d) => {
    try {
        return new Date(d).toLocaleString("en-ZA", {
            day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch (_) {
        return d;
    }
};

export default function AdminInquiries() {
    const [authed, setAuthed] = useState(null); // null = checking, true/false once resolved

    useEffect(() => {
        fetchCurrentAdmin()
            .then(() => setAuthed(true))
            .catch(() => setAuthed(false));
    }, []);

    if (authed === null) {
        return (
            <div className="min-h-[80vh] bg-tca-fog flex items-center justify-center" data-testid="admin-checking-session">
                <Loader2 className="w-6 h-6 animate-spin text-tca-orange" />
            </div>
        );
    }

    if (!authed) {
        return <AdminLogin onSuccess={() => setAuthed(true)} />;
    }
    return (
        <Dashboard
            onLogout={async () => {
                await adminLogout().catch(() => {});
                setAuthed(false);
            }}
        />
    );
}

function AdminLogin({ onSuccess }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) return;
        setLoading(true);
        setError("");
        try {
            await adminLogin(email, password);
            toast.success("Welcome back.");
            onSuccess();
        } catch (err) {
            const status = err?.response?.status;
            const msg =
                status === 401
                    ? "Incorrect email or password."
                    : status === 429
                    ? "Too many failed attempts. Please try again in 15 minutes."
                    : "Login failed. Please try again.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] bg-tca-fog flex items-center justify-center px-5" data-testid="admin-login">
            <div className="w-full max-w-md bg-white border border-tca-line p-8 sm:p-10">
                <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4 text-tca-orange" />
                    <p className="overline text-tca-orange">Admin · TC Auditors</p>
                </div>
                <h1 className="font-display font-extrabold text-tca-ink text-3xl mb-2">Sign in</h1>
                <p className="text-sm text-tca-mute mb-7 leading-relaxed">
                    Enter your admin email and password to view all website inquiries and lead-magnet captures.
                </p>
                <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
                    <div>
                        <label className="overline mb-2 block">Admin email</label>
                        <input
                            type="email"
                            autoFocus
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="admin-login-email"
                            className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                            placeholder="Please enter your email address"
                        />
                    </div>
                    <div>
                        <label className="overline mb-2 block">Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="admin-login-password"
                                className="w-full border border-tca-line bg-white px-4 py-3 pr-11 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                                placeholder="••••••••••"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((s) => !s)}
                                data-testid="admin-login-password-toggle"
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                className="absolute inset-y-0 right-0 flex items-center px-3 text-tca-mute hover:text-tca-orange transition-colors"
                            >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {error && <p className="text-xs text-red-600 mt-2" data-testid="admin-login-error">{error}</p>}
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="admin-login-submit">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Dashboard({ onLogout }) {
    const [inquiries, setInquiries] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [source, setSource] = useState("");
    const [status, setStatus] = useState("");
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [checkedIds, setCheckedIds] = useState(new Set());
    const [resendingId, setResendingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setCheckedIds(new Set());
        try {
            const [list, st] = await Promise.all([
                fetchInquiries({ source: source || undefined, status: status || undefined, limit: 200 }),
                fetchAdminStats(),
            ]);
            setInquiries(list);
            setStats(st);
        } catch (err) {
            if (err?.response?.status === 401) {
                toast.error("Session expired. Please sign in again.");
                onLogout();
            } else {
                toast.error("Could not load inquiries.");
            }
        } finally {
            setLoading(false);
        }
    }, [source, status, onLogout]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return inquiries;
        return inquiries.filter((i) =>
            (i.name + " " + i.email + " " + (i.company || "") + " " + (i.subject || "") + " " + i.message)
                .toLowerCase()
                .includes(q),
        );
    }, [inquiries, search]);

    const toggleChecked = (id) => {
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const allShownChecked = filtered.length > 0 && filtered.every((i) => checkedIds.has(i.id));
    const someShownChecked = filtered.some((i) => checkedIds.has(i.id));
    const selectAllRef = useRef(null);

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someShownChecked && !allShownChecked;
        }
    }, [someShownChecked, allShownChecked]);

    const toggleSelectAllShown = () => {
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (allShownChecked) {
                filtered.forEach((i) => next.delete(i.id));
            } else {
                filtered.forEach((i) => next.add(i.id));
            }
            return next;
        });
    };

    const onStatusChange = async (id, newStatus) => {
        try {
            const updated = await updateInquiryStatus(id, newStatus);
            setInquiries((rows) => rows.map((r) => (r.id === id ? updated : r)));
            if (selected?.id === id) setSelected(updated);
            toast.success(`Marked as ${newStatus}.`);
            // Refresh stats (counts may have shifted)
            fetchAdminStats().then(setStats).catch(() => {});
        } catch (err) {
            toast.error("Could not update status.");
        }
    };

    const [confirmState, setConfirmState] = useState(null); // { message, action } | null
    const askConfirm = (message, action) => setConfirmState({ message, action });

    const onDelete = (id, name) => {
        askConfirm(`Delete inquiry from ${name}? This cannot be undone.`, async () => {
            try {
                await deleteInquiry(id);
                setInquiries((rows) => rows.filter((r) => r.id !== id));
                setCheckedIds((prev) => {
                    if (!prev.has(id)) return prev;
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
                if (selected?.id === id) setSelected(null);
                toast.success("Inquiry deleted.");
                fetchAdminStats().then(setStats).catch(() => {});
            } catch (err) {
                toast.error("Could not delete inquiry.");
            }
        });
    };

    const onDeleteSelected = () => {
        const ids = Array.from(checkedIds);
        if (!ids.length) return;
        askConfirm(`Delete ${ids.length} selected ${ids.length > 1 ? "inquiries" : "inquiry"}? This cannot be undone.`, async () => {
            try {
                await bulkDeleteInquiries(ids);
                setInquiries((rows) => rows.filter((r) => !checkedIds.has(r.id)));
                if (selected && checkedIds.has(selected.id)) setSelected(null);
                setCheckedIds(new Set());
                toast.success(`Deleted ${ids.length} ${ids.length > 1 ? "inquiries" : "inquiry"}.`);
                fetchAdminStats().then(setStats).catch(() => {});
            } catch (err) {
                toast.error("Could not delete selected inquiries.");
            }
        });
    };

    const onResend = async (id) => {
        setResendingId(id);
        try {
            const { email_status } = await resendInquiryEmail(id);
            setInquiries((rows) => rows.map((r) => (r.id === id ? { ...r, email_status } : r)));
            if (selected?.id === id) setSelected((s) => ({ ...s, email_status }));
            if (email_status === "sent") {
                toast.success("Email resent successfully.");
            } else {
                toast.error("Resend failed — Microsoft Graph is still returning an error.");
            }
        } catch (err) {
            toast.error("Could not resend email. Please try again.");
        } finally {
            setResendingId(null);
        }
    };

    const onDeleteAll = () => {
        if (!(stats?.total || inquiries.length)) return;
        askConfirm(`Delete ALL ${stats?.total ?? inquiries.length} inquiries from the database? This cannot be undone.`, async () => {
            try {
                await deleteAllInquiries();
                setSelected(null);
                toast.success("All inquiries deleted.");
                load();
            } catch (err) {
                toast.error("Could not delete all inquiries.");
            }
        });
    };

    return (
        <div className="bg-tca-fog min-h-screen" data-testid="admin-dashboard">
            {/* Toolbar */}
            <div className="bg-white border-b border-tca-line">
                <div className="container-tca py-4 flex items-center justify-between gap-3">
                    <div>
                        <p className="overline text-tca-orange">Admin · TC Auditors</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-xl">Website inquiries</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load} className="btn-ghost" data-testid="admin-refresh">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </button>
                        <button onClick={onLogout} className="btn-ghost text-red-600 hover:text-red-700" data-testid="admin-logout">
                            <LogOut className="w-4 h-4" /> Sign out
                        </button>
                    </div>
                </div>
            </div>

            <div className="container-tca py-8">
                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-tca-line border border-tca-line mb-8" data-testid="admin-stats">
                        <Stat label="Total" value={stats.total} icon={Inbox} />
                        <Stat label="New" value={stats.by_status.new || 0} icon={AlertCircle} highlight />
                        <Stat label="Read" value={stats.by_status.read || 0} icon={BookOpen} />
                        <Stat label="Responded" value={stats.by_status.responded || 0} icon={CheckCircle2} />
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white border border-tca-line p-4 mb-6 grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="admin-filters">
                    <div className="md:col-span-5 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tca-mute" />
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, email, company, message..."
                            data-testid="admin-search"
                            className="w-full pl-10 pr-9 py-2.5 border border-tca-line bg-white text-sm focus:outline-none focus:border-tca-orange"
                        />
                        {search && (
                            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-tca-mute hover:text-tca-ink" data-testid="admin-search-clear">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                    <select
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        data-testid="admin-source-filter"
                        className="md:col-span-4 border border-tca-line bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-tca-orange"
                    >
                        {SOURCES.map((s) => (
                            <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                    </select>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        data-testid="admin-status-filter"
                        className="md:col-span-3 border border-tca-line bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-tca-orange"
                    >
                        {STATUSES.map((s) => (
                            <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                    </select>
                </div>

                {/* Bulk actions */}
                <div className="flex items-center gap-2 mb-4" data-testid="admin-bulk-actions">
                    <button
                        onClick={onDeleteAll}
                        className="btn-ghost text-red-600 hover:text-red-700 border border-red-200"
                        data-testid="admin-delete-all"
                    >
                        <Trash2 className="w-4 h-4" /> Delete All
                    </button>
                    {checkedIds.size > 0 && (
                        <button
                            onClick={onDeleteSelected}
                            className="btn-ghost text-red-600 hover:text-red-700 border border-red-200 bg-red-50"
                            data-testid="admin-delete-selected"
                        >
                            <Trash2 className="w-4 h-4" /> Delete Selected ({checkedIds.size})
                        </button>
                    )}
                </div>

                {/* List */}
                <div className="bg-white border border-tca-line overflow-hidden" data-testid="admin-inquiry-table">
                    {loading ? (
                        <div className="py-20 flex items-center justify-center text-tca-mute">
                            <Loader2 className="w-6 h-6 animate-spin" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-16 text-center" data-testid="admin-empty">
                            <p className="font-display font-bold text-tca-ink mb-1">No inquiries to show</p>
                            <p className="text-sm text-tca-mute">Try adjusting filters or wait for the next submission.</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 px-5 py-2.5 border-b border-tca-line bg-tca-fog/50">
                                <input
                                    ref={selectAllRef}
                                    type="checkbox"
                                    checked={allShownChecked}
                                    onChange={toggleSelectAllShown}
                                    data-testid="admin-select-all-shown"
                                    className="w-4 h-4 accent-tca-orange cursor-pointer"
                                />
                                <label
                                    onClick={toggleSelectAllShown}
                                    className="text-xs uppercase tracking-[0.12em] text-tca-mute font-bold cursor-pointer"
                                >
                                    Select all shown ({filtered.length})
                                </label>
                            </div>
                            <ul className="divide-y divide-tca-line">
                            {filtered.map((i) => (
                                <li
                                    key={i.id}
                                    data-testid={`admin-inquiry-${i.id}`}
                                    className={`flex items-start gap-3 px-5 py-4 hover:bg-tca-fog cursor-pointer ${
                                        selected?.id === i.id ? "bg-tca-orangeSoft/30" : ""
                                    }`}
                                    onClick={() => setSelected(i)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checkedIds.has(i.id)}
                                        onChange={() => toggleChecked(i.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={`admin-inquiry-checkbox-${i.id}`}
                                        className="mt-1.5 w-4 h-4 accent-tca-orange shrink-0 cursor-pointer"
                                    />
                                    <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-3 lg:items-center">
                                        <div className="lg:col-span-3">
                                            <p className="font-display font-bold text-tca-ink">{i.name}</p>
                                            <p className="text-xs text-tca-mute">{i.email}</p>
                                            {i.email_status === "failed" && (
                                                <span className="mt-1.5" data-testid={`admin-email-failed-badge-${i.id}`}>
                                                    <EmailFailedBadge />
                                                </span>
                                            )}
                                        </div>
                                        <div className="lg:col-span-3 text-xs text-tca-mute">
                                            <p>{SOURCE_LABEL[i.source] || i.source}</p>
                                            {i.company && <p className="text-tca-ink">{i.company}</p>}
                                        </div>
                                        <div className="lg:col-span-3 text-sm text-tca-ink truncate">
                                            {i.subject || "—"}
                                        </div>
                                        <div className="lg:col-span-2 text-xs text-tca-mute">{formatDate(i.created_at)}</div>
                                        <div className="lg:col-span-1 flex justify-end items-center gap-2">
                                            <StatusPill status={i.status} />
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDelete(i.id, i.name);
                                                }}
                                                data-testid={`admin-inquiry-delete-${i.id}`}
                                                className="p-1.5 text-tca-mute hover:text-red-600 transition-colors"
                                                title="Delete inquiry"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                            </ul>
                        </>
                    )}
                </div>
            </div>

            {/* Detail drawer */}
            {selected && (
                <DetailDrawer
                    inquiry={selected}
                    onClose={() => setSelected(null)}
                    onStatus={(s) => onStatusChange(selected.id, s)}
                    onDelete={() => onDelete(selected.id, selected.name)}
                    onResend={() => onResend(selected.id)}
                    resending={resendingId === selected.id}
                />
            )}

            {/* Confirm dialog — custom (not window.confirm, which browsers silently
                block inside cross-origin iframes such as an embedded preview pane) */}
            {confirmState && (
                <ConfirmDialog
                    message={confirmState.message}
                    onCancel={() => setConfirmState(null)}
                    onConfirm={() => {
                        confirmState.action();
                        setConfirmState(null);
                    }}
                />
            )}
        </div>
    );
}

function Stat({ label, value, icon: Icon, highlight }) {
    return (
        <div className={`bg-white p-5 ${highlight ? "border-l-4 border-tca-orange" : ""}`} data-testid={`admin-stat-${label.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-2">
                <p className="overline">{label}</p>
                <Icon className="w-4 h-4 text-tca-mute" />
            </div>
            <p className="font-display font-extrabold text-tca-ink text-3xl tabular-nums">{value}</p>
        </div>
    );
}

function StatusPill({ status }) {
    const t = STATUS_TONE[status] || STATUS_TONE.new;
    return (
        <span
            data-testid={`status-pill-${status}`}
            className={`inline-block text-[10px] uppercase tracking-[0.16em] font-bold px-2.5 py-1 ${t.bg} ${t.text} border ${t.border}`}
        >
            {status}
        </span>
    );
}

function EmailFailedBadge() {
    return (
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-bold px-2 py-1 bg-red-50 text-red-700 border border-red-300">
            <MailWarning className="w-3 h-3" /> Email failed
        </span>
    );
}

function ConfirmDialog({ message, onCancel, onConfirm }) {
    return (
        <div
            className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center px-5"
            onClick={onCancel}
            data-testid="admin-confirm-dialog"
        >
            <div
                className="bg-white w-full max-w-sm border border-tca-line p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <p className="text-sm text-tca-ink leading-relaxed mb-6" data-testid="admin-confirm-message">
                    {message}
                </p>
                <div className="flex gap-3 justify-end">
                    <button onClick={onCancel} className="btn-ghost" data-testid="admin-confirm-cancel">
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="btn-primary bg-red-600 border-red-600 hover:bg-red-700 hover:border-red-700"
                        data-testid="admin-confirm-delete"
                    >
                        <Trash2 className="w-4 h-4" /> Delete
                    </button>
                </div>
            </div>
        </div>
    );
}

function DetailDrawer({ inquiry, onClose, onStatus, onDelete, onResend, resending }) {
    const emailStatus = inquiry.email_status || "pending";
    const emailTone =
        emailStatus === "sent"
            ? { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300", label: "Delivered" }
            : emailStatus === "failed"
            ? { bg: "bg-red-50", text: "text-red-700", border: "border-red-300", label: "Failed to send" }
            : { bg: "bg-tca-fog", text: "text-tca-mute", border: "border-tca-line", label: "Pending / not tracked" };
    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex justify-end"
            onClick={onClose}
            data-testid="admin-detail-drawer"
        >
            <div
                className="bg-white w-full max-w-xl h-full overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="border-b border-tca-line p-6 sticky top-0 bg-white flex items-start justify-between gap-3">
                    <div>
                        <p className="overline text-tca-orange">{SOURCE_LABEL[inquiry.source] || inquiry.source}</p>
                        <h2 className="font-display font-extrabold text-tca-ink text-2xl mt-1">{inquiry.name}</h2>
                        <p className="text-xs text-tca-mute mt-1">{formatDate(inquiry.created_at)}</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-tca-mute hover:text-tca-ink" data-testid="admin-detail-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    <div className="flex items-center justify-between">
                        <StatusPill status={inquiry.status} />
                        <div className="flex gap-1.5">
                            {["new", "read", "responded"].map((s) => (
                                <button
                                    key={s}
                                    onClick={() => onStatus(s)}
                                    disabled={inquiry.status === s}
                                    data-testid={`admin-detail-mark-${s}`}
                                    className={`text-[10px] uppercase tracking-[0.16em] font-bold px-3 py-1.5 border transition ${
                                        inquiry.status === s
                                            ? "bg-tca-ink text-white border-tca-ink"
                                            : "bg-white text-tca-ink border-tca-line hover:border-tca-orange hover:text-tca-orange"
                                    }`}
                                >
                                    Mark {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div
                        className={`flex items-center justify-between gap-3 border ${emailTone.border} ${emailTone.bg} p-3`}
                        data-testid="admin-detail-email-status"
                    >
                        <div className="flex items-center gap-2">
                            <MailWarning className={`w-4 h-4 ${emailTone.text}`} />
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.18em] text-tca-mute font-bold">Email delivery</p>
                                <p className={`text-sm font-bold ${emailTone.text}`}>{emailTone.label}</p>
                            </div>
                        </div>
                        {emailStatus === "failed" && (
                            <button
                                onClick={onResend}
                                disabled={resending}
                                data-testid="admin-detail-resend"
                                className="btn-ghost text-tca-orange border border-tca-orange hover:bg-tca-orangeSoft disabled:opacity-60 px-3"
                            >
                                {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {resending ? "Sending..." : "Resend email"}
                            </button>
                        )}
                    </div>

                    <div className="space-y-3">
                        <DetailRow icon={Mail} label="Email" value={
                            <a href={`mailto:${inquiry.email}?subject=Re: ${encodeURIComponent(inquiry.subject || "Your inquiry")}`} className="text-tca-orange hover:underline">
                                {inquiry.email}
                            </a>
                        } />
                        {inquiry.phone && <DetailRow icon={Phone} label="Phone" value={
                            <a href={`tel:${inquiry.phone}`} className="text-tca-ink hover:text-tca-orange">{inquiry.phone}</a>
                        } />}
                        {inquiry.company && <DetailRow icon={Building2} label="Company" value={inquiry.company} />}
                        {inquiry.subject && <DetailRow icon={Inbox} label="Subject" value={inquiry.subject} />}
                        <DetailRow icon={Calendar} label="Submitted" value={formatDate(inquiry.created_at)} />
                    </div>

                    <div>
                        <p className="overline mb-2">Message</p>
                        <div
                            className="border-l-4 border-tca-orange bg-tca-orangeSoft/40 p-4 text-sm text-tca-ink whitespace-pre-wrap leading-relaxed"
                            data-testid="admin-detail-message"
                        >
                            {inquiry.message}
                        </div>
                    </div>

                    <div className="border-t border-tca-line pt-5 flex gap-3">
                        <a
                            href={`mailto:${inquiry.email}?subject=Re: ${encodeURIComponent(inquiry.subject || "Your inquiry")}`}
                            className="btn-primary flex-1"
                            data-testid="admin-detail-reply"
                        >
                            <Mail className="w-4 h-4" /> Reply via email
                        </a>
                        <button
                            onClick={onDelete}
                            className="btn-ghost text-red-600 hover:text-red-700 border border-tca-line px-4"
                            data-testid="admin-detail-delete"
                        >
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function DetailRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3 text-sm">
            <Icon className="w-4 h-4 mt-0.5 text-tca-orange shrink-0" />
            <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-tca-mute font-bold">{label}</p>
                <p className="text-tca-ink font-medium">{value}</p>
            </div>
        </div>
    );
}
